import BaseObject from "sap/ui/base/Object";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import ODataModelHandler from "ips/ac/util/classes/odata/ODataModelHandler";
import { ClientConfiguration, UserConfiguration } from "ips/ac/model/odata/generated/IpsCoreModel";
import Context from "sap/ui/model/odata/v4/Context";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import View from "sap/ui/core/mvc/View";
import { AclDto, UserDto } from "ips/ac/model/api/WinDoFlow.api.types";
import Util from "ips/ac/util/Util";
import JSONModel from "sap/ui/model/json/JSONModel";
import Client from "ips/ac/data/client/Client";
import Localization from "sap/base/i18n/Localization";
import ClientConfigurationMapper from "../client/ClientConfigurationMapper";
import UserConfigurationMapper from "./UserConfigurationMapper";

type AclConfig = {
  view: boolean,
  edit: boolean,
  delete: boolean,
  create: boolean,
}

export default class User extends BaseObject {
  private static instance: User;

  private guid: string;
  private firstName: string;
  private lastName: string;
  private language: string;
  private imageGuid: string;

  private roles: string[] = [];
  private aclConfig: Record<string, AclConfig> = {};
  private configurationDetails: UserConfiguration[] = [];
  private clientConfigurations: ClientConfiguration[] = [];

  private _oDataModelHandler: ODataModelHandler;

  private client: Client;

  private constructor() {
    super();
  }

  public static getInstance(): User {
    if (!User.instance) {
      User.instance = new User();
    }
    return User.instance;
  }

  public setData(user: UserDto): void {
    this.guid = user.guid;
    this.firstName = user.firstName;
    this.lastName = user.lastName;
    this.imageGuid = user.imageGuid;
    this._oDataModelHandler = new ODataModelHandler();
    this.configurationDetails = UserConfigurationMapper.dtoListToUserConfigurationList(user.configurations);
    this.clientConfigurations = ClientConfigurationMapper.dtoListToClientConfigurationList(user.client.configurations);

    this.language = user.language;
    Localization.setLanguage(user.language);
    this.roles = user.roles ?? [];
    this.aclConfig = this.buildAclConfig(user.acl);

    this.client = new Client(user.client);
  }

  /**
   * Pushes the already-loaded user/client data into the Component's "app" JSONModel.
   * Must only be called once the Component (and therefore its app model) exists.
   */
  public syncAppModel(): void {
    const appModel = Util.getComponent().getAppModel();

    const userRoles: Record<string, boolean> = Object.fromEntries(
      this.roles.map((role) => [role, true] as const),
    );
    appModel.setProperty("/UserRoles", userRoles);
    appModel.setProperty("/UserAcl/", this.aclConfig);
    appModel.setProperty("/selectedLanguage", this.language);

    const userObject = {
      ImageFileGuid: this.imageGuid,
    };
    // todo Type_Id, Type_Value, Language_Value missing
    Object.defineProperty(userObject, "FullName", { get: () => this.getFullName() });
    Object.defineProperty(userObject, "isPlannerOrAdministrator", { get: () => this.hasRole("CLIENT_ADMINISTRATOR") || this.hasRole("IPS_PLANNER") });
    appModel.setProperty("/User", userObject);

    this.client.syncAppModel();
  }

  private buildAclConfig(acls: AclDto[]): Record<string, AclConfig> {
    const aclConfig: Record<string, AclConfig> = {};

    acls.forEach((acl: AclDto) => {
      if (!aclConfig[acl.instanceType]) {
        aclConfig[acl.instanceType] = {
          view: false,
          edit: false,
          delete: false,
          create: false,
        };
      }

      const actionType = acl.actionType.toLowerCase() as keyof AclConfig;
      aclConfig[acl.instanceType][actionType] = true;
    });

    return aclConfig;
  };

  public setLanguage(language: string) {
    this.language = language;
    Util.getComponent().getAppModel().setProperty("/selectedLanguage", language);
    Localization.setLanguage(language);
  };

  public getGuid(): string {
    return this.guid;
  }

  public getFullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  public getLanguage(): string {
    return this.language;
  }

  public getUserConfiguration(): UserConfiguration[] {
    return this.configurationDetails;
  }

  public getClientConfiguration(): ClientConfiguration[] {
    return this.clientConfigurations;
  }

  public getClientGuid(): string {
    return this.getClient().getGuid();
  }

  public hasRole(role: string): boolean {
    return this.roles.includes(role);
  }

  public getModel(): JSONModel {
    return Util.getComponent().getAppModel();
  }

  public getClient(): Client {
    return this.client;
  }

  public setClientConfigProperty(configName: string, value: string): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const targetConfig = {
          ApplicationId: "APP_COCKPIT_ID",
          ConfigurationId: configName,
          Value: value,
          ClientGuid: this.getClientGuid(),
        };

        const loadedConfig: any = this.getClientConfiguration()?.find(o => o.ConfigurationId === targetConfig.ConfigurationId);

        const data: any = loadedConfig
          ? await this._updateLoadedConfig(loadedConfig, targetConfig, "ClientConfigurations")
          : await this._createTargetConfig(targetConfig, "ClientConfigurations");

        resolve(data);
      } catch (e) {
        reject(e);
      }
    });
  }

  public setUserConfigProperty(configName: string, value: string): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const targetConfig = {
          ApplicationId: "APP_COCKPIT_ID",
          ConfigurationId: configName,
          Value: value,
        };

        const loadedConfig: any = this.getUserConfiguration()?.find(o => o?.ConfigurationId === targetConfig.ConfigurationId);

        const data: any = loadedConfig
          ? await this._updateLoadedConfig(loadedConfig, targetConfig, "UserConfigurations")
          : await this._createTargetConfig(targetConfig, "UserConfigurations");
        if(loadedConfig) {
          loadedConfig.Value = value;
        }
        resolve(data);
      } catch (e) {
        reject(e);
      }
    });
  }

  private _updateLoadedConfig(loadedConfig: any, targetConfig: any, entitySet: string): Promise<any> {
    const sid: string = loadedConfig.Sid;
    const path: string = `/${entitySet}('${sid}')`;

    return this._oDataModelHandler.update(path, targetConfig);
  }

  private async _createTargetConfig(targetConfig: any, path: string): Promise<any> {
    return this._oDataModelHandler.create(`/${path}`, targetConfig).then((data: any) => {
      if (path === "ClientConfigurations") {
        this.clientConfigurations.push(data);
      } else {
        this.configurationDetails.push(data);
      }
      return data;
    });
  }

  public getUserConfigurationById(id: string): UserConfiguration | null {
    if (!id) {
      console.error("no configuration id provided");
      return null;
    }
    const configuration = this.getUserConfiguration();
    return configuration?.find(o => o.ConfigurationId === id);
  }

  // TODO remove with old erp Generator
  public async getErpConfigValue(view: View, typeValue: string): Promise<any | null> {
    const model: ODataModel = view.getModel() as ODataModel;
    const clientGuid: string = this.getClientGuid();

    const filters: Filter[] = [
      new Filter("ClientGuid", FilterOperator.EQ, clientGuid),
      new Filter("TypeValue", FilterOperator.EQ, typeValue),
    ];

    const listBinding: ODataListBinding = model.bindList("/ErpConfigs", undefined, undefined, filters);
    const aCtx: Context[] = await listBinding.requestContexts(0, 1);

    return aCtx[0] ? await aCtx[0].requestObject() : null;
  }

  public isConfigurationEnabled(systemId: string): boolean {
    return this.getUserConfiguration().some(c => c.ConfigurationId === systemId && c.Value === "1") ?? false;
  }
}
